/**
 * Payload for Firestore when an order is marked fully delivered:
 * all quantified line items get deliveredQuantity = ordered qty, ongoing status cleared.
 */

function normalizeToArray<T>(value: T | T[] | null | undefined): T[] {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

function toNumber(value: any): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function mapLineItemFullyDelivered(item: any): any {
  const qty = toNumber(item?.qty);
  const quantity = toNumber(item?.quantity);
  const requiredQty = qty !== null ? qty : quantity;
  if (requiredQty === null || requiredQty <= 0) {
    return item;
  }
  return { ...item, deliveredQuantity: requiredQty };
}

function mapLineArray(arr: any[] | undefined): any[] | undefined {
  if (!arr || !Array.isArray(arr)) {
    return arr;
  }
  return arr.map(mapLineItemFullyDelivered);
}

function mapSingleOption(opt: any): any {
  if (!opt || typeof opt !== 'object') {
    return opt;
  }
  return mapLineItemFullyDelivered(opt);
}

function isLineItemCompleted(lineItem: any): boolean {
  const qty = toNumber(lineItem?.qty);
  const quantity = toNumber(lineItem?.quantity);
  const requiredQty = qty !== null ? qty : quantity;

  if (requiredQty === null || requiredQty <= 0) {
    return true;
  }

  const deliveredQty = toNumber(lineItem?.deliveredQuantity) ?? 0;
  return deliveredQty >= requiredQty;
}

/**
 * True when every quantified line (items, extras, product options, etc.) is fully delivered.
 * Aligns with order-table Past / Ongoing eligibility.
 */
export function isOrderFullyDelivered(order: any): boolean {
  const lineItems = [
    ...normalizeToArray(order?.items),
    ...normalizeToArray(order?.serviceExtras),
    ...normalizeToArray(order?.extraCharges),
    ...normalizeToArray(order?.serviceType),
    ...normalizeToArray(order?.productOptions),
  ];

  const deliverableItems = lineItems.filter((item) => {
    const qty = toNumber(item?.qty);
    const quantity = toNumber(item?.quantity);
    const requiredQty = qty !== null ? qty : quantity;
    return requiredQty !== null && requiredQty > 0;
  });

  if (deliverableItems.length === 0) {
    return !!order?.delivered;
  }

  return deliverableItems.every((item) => isLineItemCompleted(item));
}

export function buildFullDeliverUpdate(order: any): Record<string, unknown> {
  const updates: Record<string, unknown> = {
    delivered: true,
    confirmed: true,
    orderDStatus: '',
    updatedAt: new Date(),
  };

  if (order?.items) {
    updates.items = mapLineArray(order.items);
  }
  if (order?.serviceExtras) {
    updates.serviceExtras = mapLineArray(order.serviceExtras);
  }
  if (order?.extraCharges) {
    updates.extraCharges = mapLineArray(order.extraCharges);
  }
  if (order?.serviceType) {
    updates.serviceType = mapSingleOption(order.serviceType);
  }
  if (order?.productOptions) {
    updates.productOptions = mapSingleOption(order.productOptions);
  }

  return updates;
}

function mapLineItemClearDelivered(item: any): any {
  const qty = toNumber(item?.qty);
  const quantity = toNumber(item?.quantity);
  const requiredQty = qty !== null ? qty : quantity;
  if (requiredQty === null || requiredQty <= 0) {
    return item;
  }
  return { ...item, deliveredQuantity: 0 };
}

function mapLineArrayClear(arr: any[] | undefined): any[] | undefined {
  if (!arr || !Array.isArray(arr)) {
    return arr;
  }
  return arr.map(mapLineItemClearDelivered);
}

function mapSingleOptionClear(opt: any): any {
  if (!opt || typeof opt !== 'object') {
    return opt;
  }
  return mapLineItemClearDelivered(opt);
}

/**
 * Firestore payload when moving an order back to Ongoing: clear top-level delivered
 * and deliveredQuantity on every quantified line (same buckets as full deliver).
 */
export function buildOngoingResetUpdate(order: any): Record<string, unknown> {
  const updates: Record<string, unknown> = {
    delivered: false,
    orderDStatus: 'ongoing',
    updatedAt: new Date(),
  };

  if (order?.items) {
    updates.items = mapLineArrayClear(order.items);
  }
  if (order?.serviceExtras) {
    updates.serviceExtras = mapLineArrayClear(order.serviceExtras);
  }
  if (order?.extraCharges) {
    updates.extraCharges = mapLineArrayClear(order.extraCharges);
  }
  if (order?.serviceType) {
    updates.serviceType = mapSingleOptionClear(order.serviceType);
  }
  if (order?.productOptions) {
    updates.productOptions = mapSingleOptionClear(order.productOptions);
  }

  return updates;
}
